// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.
import { z } from 'zod';
import { CaseStatus, SubStatus, ClosureReason } from '../constants/index.js';
const allStatuses = Object.values(CaseStatus);
const allSubStatuses = Object.values(SubStatus);
const allClosureReasons = Object.values(ClosureReason);
// Sprint 43.0.0 — Rights Request workflow completion enums
const rejectionReasonCodes = [
    'identity_not_verified',
    'manifestly_unfounded',
    'repetitive',
    'exempt',
    'out_of_scope',
    'duplicate',
];
const responseDeliveryMethods = ['email', 'portal', 'postal', 'in_person'];
const assignedDepartmentSchema = z
    .object({
    id: z.string().min(1),
    name: z.string().min(1),
})
    .nullable();
/**
 * Validation schema for updating a case (PATCH).
 * See: docs/backend/api-contracts.md - PATCH /api/v1/cases/:case_id
 */
export const updateCaseSchema = z
    .object({
    status: z.enum(allStatuses).optional(),
    sub_status: z.enum(allSubStatuses).nullable().optional(),
    assigned_to: z.string().nullable().optional(),
    description: z.string().max(10000).nullable().optional(),
    closure_summary: z.string().max(10000).nullable().optional(),
    closure_reason: z.enum(allClosureReasons).nullable().optional(),
    // Sprint 43.0.0 — Rights Request workflow completion fields
    assigned_department: assignedDepartmentSchema.optional(),
    extension_reason: z.string().max(2000).nullable().optional(),
    extension_notified_at: z.string().datetime().nullable().optional(),
    rejection_reason_code: z.enum(rejectionReasonCodes).nullable().optional(),
    rejection_reason_description: z.string().max(2000).nullable().optional(),
    response_delivery_method: z.enum(responseDeliveryMethods).nullable().optional(),
    response_delivered_at: z.string().datetime().nullable().optional(),
    // Sprint 43.0.0 — pause_reason is required when transitioning into a
    // SLA-paused (waiting_*) status; service-layer validation enforces this.
    pause_reason: z
        .enum(['awaiting_subject', 'awaiting_verification', 'third_party_dependency', 'other'])
        .optional(),
})
    .refine((data) => Object.keys(data).length > 0, {
    message: 'At least one field must be provided for update',
});
/**
 * Validation schema for adding a note to a case.
 */
export const addNoteSchema = z.object({
    content: z.string().min(1, 'Note content is required').max(5000),
    visibility: z.enum(['internal']).default('internal'),
    note_type: z
        .enum(['comment', 'status_change', 'sla_event', 'verification_event', 'system'])
        .default('comment'),
});
/**
 * Query parameter validation for case list endpoint.
 */
export const caseListQuerySchema = z.object({
    status: z.string().optional(),
    sub_status: z.string().optional(),
    request_type: z.string().optional(),
    assigned_to: z.string().optional(),
    business_unit_id: z.string().optional(),
    sla_breached: z.enum(['true', 'false']).optional(),
    search: z.string().max(200).optional(),
    created_after: z.string().datetime().optional(),
    created_before: z.string().datetime().optional(),
    page: z.coerce.number().int().min(1).default(1),
    per_page: z.coerce.number().int().min(1).max(100).default(25),
    sort: z
        .string()
        .regex(/^[a-z_]+:(asc|desc)$/)
        .default('created_at:desc'),
});
//# sourceMappingURL=case.js.map